import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./ModIndex.css";
import { ReactComponent as Undo } from "../../../../../icons/undo.svg";
import ModFilter from "../ModFilters/ModFilter";
import { normalizeDisplayLabel } from "../../../../../utility/displayText";

// ─── Types ───────────────────────────────────────────────
interface SlotData {
  capacity: number;
  technical_minimum: number;
  class: number;
}

interface GeneratorRow {
  name: string;
  vc: number;
  slots: SlotData[];
}

// 96 slots × 15 min
const generateTimeRanges = (): string[] => {
  const ranges: string[] = [];
  for (let s = 0; s < 96; s++) {
    const startMin = s * 15;
    const endMin = startMin + 15;
    const fmt = (m: number) => {
      const h = Math.floor(m / 60) % 24;
      const mm = m % 60;
      return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    };
    ranges.push(`${fmt(startMin)} - ${fmt(endMin)}`);
  }
  return ranges;
};

const TIME_RANGES = generateTimeRanges();

const SLOT_FIELDS = ["capacity", "technical_minimum", "class"] as const;
type SlotField = (typeof SLOT_FIELDS)[number];

type BlockSelection =
  | number
  | {
      value?: number | string;
      label?: number | string;
    };

const resolveBlockNumber = (block: BlockSelection): number | null => {
  if (typeof block === "number") {
    return Number.isFinite(block) ? block : null;
  }

  if (block && typeof block === "object") {
    const rawValue = block.value ?? block.label;
    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const getVisibleSlots = (selectedBlock: BlockSelection[]): number[] => {
  const allSlots = TIME_RANGES.map((_, i) => i);

  if (!Array.isArray(selectedBlock) || selectedBlock.length === 0) {
    return allSlots;
  }

  const normalizedSlots = selectedBlock
    .map(resolveBlockNumber)
    .filter(
      (slot): slot is number =>
        slot !== null && slot >= 1 && slot <= TIME_RANGES.length,
    )
    .map((slot) => slot - 1);

  if (!normalizedSlots.length) return allSlots;
  return Array.from(new Set(normalizedSlots)).sort((a, b) => a - b);
};

const toRenderableValue = (value: any) => {
  if (value == null) return "";
  if (typeof value === "object") {
    if ("label" in value && value.label != null) return String(value.label);
    if ("value" in value && value.value != null) return String(value.value);
    return "";
  }
  return value;
};

// ─── Transform flat API rows → GeneratorRow[] ────────────
function buildRows(apiData: any[]): GeneratorRow[] {
  if (!Array.isArray(apiData) || apiData.length === 0) return [];

  // Collect unique generators preserving first-seen order
  const generatorOrder: string[] = [];
  const generatorVc: Record<string, number> = {};
  for (const row of apiData) {
    if (!generatorOrder.includes(row.generator)) {
      generatorOrder.push(row.generator);
      generatorVc[row.generator] = row.vc ?? 0;
    }
  }

  // Build a lookup: generatorName → slotIndex (1-based) → SlotData
  const slotMap: Record<string, Record<number, SlotData>> = {};
  for (const row of apiData) {
    const gen = row.generator;
    const slot = Number(row.time_slot__slot);
    if (!slotMap[gen]) slotMap[gen] = {};
    slotMap[gen][slot] = {
      capacity: row.total_capacity ?? 0,
      technical_minimum: row.must_run ?? 0,
      class: row.class ?? 0,
    };
  }

  return generatorOrder.map((name) => ({
    name,
    vc: generatorVc[name],
    slots: TIME_RANGES.map((_, i) => {
      const slotIdx = i + 1; // API slots are 1-based
      return (
        slotMap[name]?.[slotIdx] ?? {
          capacity: 0,
          technical_minimum: 0,
          class: 0,
        }
      );
    }),
  }));
}

// ─── Props ──────────────────────────────────────────────
interface ModTableProps {
  maxHeight?: string;
  mode?: "normal" | "edit";
  data?: any[];
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit?: (data: any) => void;
  loading?: boolean;
  setModalFilter?: React.Dispatch<React.SetStateAction<boolean>>;
  handleUpdateReview?: (data: any) => void;
  modalFilter?: boolean;
  uniqueGeneratorType?: any[];
  uniqueOwnership?: any[];
  uniqueBlock?: any[];
  selectedBlock?: BlockSelection[];
}

interface EditingCell {
  gen: string;
  slot: number;
  field: SlotField;
}

interface EditingVc {
  gen: string;
}

// ─── Sort indicator ──────────────────────────────────────
function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  return (
    <span
      className={`text-[10px] leading-none ${active ? "text-[#34656D]" : "text-gray-300"}`}
    >
      {active && dir === "desc" ? "▼" : "▲"}
    </span>
  );
}

// ─── Component ───────────────────────────────────────────
export function ModTable({
  maxHeight = "calc(-300px + 100vh)",
  mode = "normal",
  data,
  setModalOpen,
  handleSubmit,
  loading = false,
  setModalFilter,
  handleUpdateReview,
  modalFilter,
  uniqueGeneratorType,
  uniqueOwnership,
  uniqueBlock,
  selectedBlock = [],
}: ModTableProps) {
  const rawRows = useMemo(() => buildRows(data ?? []), [data]);

  // Visible slot indices (0-based). When no blocks selected, show all 96.
  const visibleSlots = useMemo(
    () => getVisibleSlots(selectedBlock),
    [selectedBlock],
  );

  // ── Sort state ──
  type SortKey =
    | { kind: "name" }
    | { kind: "vc" }
    | { kind: "slot"; slot: number; field: SlotField };
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const toggleSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      const same =
        prev !== null && JSON.stringify(prev) === JSON.stringify(key);
      if (same) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortDir("asc");
      }
      return key;
    });
  }, []);

  const rows = useMemo(() => {
    if (!sortKey) return rawRows;
    return [...rawRows].sort((a, b) => {
      let av: any, bv: any;
      if (sortKey.kind === "name") {
        av = a.name.toLowerCase();
        bv = b.name.toLowerCase();
      } else if (sortKey.kind === "vc") {
        av = a.vc ?? 0;
        bv = b.vc ?? 0;
      } else {
        av = a.slots[sortKey.slot]?.[sortKey.field] ?? 0;
        bv = b.slots[sortKey.slot]?.[sortKey.field] ?? 0;
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [rawRows, sortKey, sortDir]);

  const totalEntities = rows.length;

  // ── Edit state ──
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [localValues, setLocalValues] = useState<Record<string, any>>({});
  const [draftValue, setDraftValue] = useState<string>("");
  const [draftError, setDraftError] = useState<string>("");
  const [localVcValues, setLocalVcValues] = useState<Record<string, number>>({});
  const [editingVc, setEditingVc] = useState<EditingVc | null>(null);
  const [draftVc, setDraftVc] = useState<string>("");
  const [draftVcError, setDraftVcError] = useState<string>("");

  const isEditMode = mode === "edit";

  const valueKey = useCallback(
    (gen: string, slot: number, field: string) => `${gen}__${slot}__${field}`,
    [],
  );

  const getOriginal = useCallback(
    (gen: string, slotIdx: number, field: SlotField) => {
      const row = rows.find((r) => r.name === gen);
      if (!row) return "-";
      return row.slots[slotIdx]?.[field] ?? "-";
    },
    [rows],
  );

  const startEdit = (gen: string, slot: number, field: SlotField) => {
    if (!isEditMode) return;
    if (
      editingCell &&
      editingCell.gen === gen &&
      editingCell.slot === slot &&
      editingCell.field === field
    )
      return;
    const k = valueKey(gen, slot, field);
    const original = getOriginal(gen, slot, field);
    const currentValue =
      localValues[k] !== undefined ? localValues[k] : original;
    setDraftValue(
      currentValue === "-" || currentValue == null ? "" : String(currentValue),
    );
    setEditingCell({ gen, slot, field });
  };

  const validateDraft = (value: string, field: SlotField): string => {
    if (value.trim() === "") return "";
    const n = Number(value);
    if (isNaN(n)) return "Must be a number";
    if (field === "technical_minimum" && (n < 1 || n > 100))
      return "Must be 1–100";
    if (field === "class" && (n < 1 || n > 5)) return "Must be 1–5";
    return "";
  };

  const confirmEdit = () => {
    if (!editingCell) return;
    const { gen, slot, field } = editingCell;
    const err = validateDraft(draftValue, field);
    if (err) { setDraftError(err); return; }
    const k = valueKey(gen, slot, field);
    const trimmed = draftValue.trim();
    let valueToSave =
      trimmed === "" ? "" : isNaN(Number(trimmed)) ? trimmed : Number(trimmed);
    if (typeof valueToSave === "number") {
      if (field === "technical_minimum") {
        valueToSave = Math.min(100, Math.max(1, valueToSave));
      } else if (field === "class") {
        valueToSave = Math.min(5, Math.max(1, valueToSave));
      }
    }
    const original = getOriginal(gen, slot, field);
    const prev = Object.prototype.hasOwnProperty.call(localValues, k)
      ? localValues[k]
      : original;
    if (valueToSave === prev) {
      setEditingCell(null);
      setDraftValue("");
      return;
    }
    setLocalValues((p) => ({ ...p, [k]: valueToSave }));
    setEditingCell(null);
    setDraftValue("");
    setDraftError("");
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setDraftValue("");
    setDraftError("");
  };

  const startEditVc = (gen: string) => {
    if (!isEditMode) return;
    if (editingVc?.gen === gen) return;
    const current = localVcValues[gen] !== undefined ? localVcValues[gen] : rows.find((r) => r.name === gen)?.vc ?? 0;
    setDraftVc(String(current));
    setEditingVc({ gen });
  };

  const confirmEditVc = () => {
    if (!editingVc) return;
    const { gen } = editingVc;
    const trimmed = draftVc.trim();
    if (trimmed === "") { setEditingVc(null); setDraftVc(""); setDraftVcError(""); return; }
    const n = Number(trimmed);
    if (isNaN(n)) { setDraftVcError("Must be a number"); return; }
    const original = rows.find((r) => r.name === gen)?.vc ?? 0;
    if (n === (localVcValues[gen] !== undefined ? localVcValues[gen] : original)) {
      setEditingVc(null); setDraftVc(""); setDraftVcError(""); return;
    }
    setLocalVcValues((p) => ({ ...p, [gen]: n }));
    setEditingVc(null); setDraftVc(""); setDraftVcError("");
  };

  const cancelEditVc = () => {
    setEditingVc(null); setDraftVc(""); setDraftVcError("");
  };

  const revertVc = (gen: string) => {
    setLocalVcValues((p) => {
      if (!(gen in p)) return p;
      const { [gen]: _, ...rest } = p;
      return rest;
    });
  };

  const revertCell = (gen: string, slot: number, field: string) => {
    const k = valueKey(gen, slot, field);
    setLocalValues((p) => {
      if (!(k in p)) return p;
      const { [k]: _, ...rest } = p;
      return rest;
    });
  };

  const revertAll = () => {
    if (!Object.keys(localValues).length && !Object.keys(localVcValues).length) return;
    setLocalValues({});
    setLocalVcValues({});
    cancelEdit();
    cancelEditVc();
  };

  const handleUpdateClick = () => {
    const merged: Record<string, any> = { ...localValues };
    // Expand per-generator VC edits into per-slot keys
    for (const [gen, vcVal] of Object.entries(localVcValues)) {
      TIME_RANGES.forEach((_, i) => {
        const k = valueKey(gen, i + 1, "vc" as any);
        merged[k] = vcVal;
      });
    }
    if (!Object.keys(merged).length) return;
    handleSubmit?.(merged);
  };

  // Bulk apply from ModFilter
  // ModFilter rows use API field names; map to UI SlotField keys.
  const API_TO_SLOT_FIELD: Record<string, SlotField> = {
    total_capacity: "capacity",
    capacity: "capacity",
    must_run: "technical_minimum",
    technical_minimum: "technical_minimum",
    class: "class",
  };
  const applyBulkChanges = useCallback(
    (changedRows: any[]) => {
      if (!Array.isArray(changedRows) || !changedRows.length) return;
      // Build original-row lookup keyed by generator+slot to diff against
      const origMap = new Map<string, any>();
      for (const o of data ?? []) {
        origMap.set(`${o.generator}__${Number(o.time_slot__slot)}`, o);
      }
      const vcUpdates: Record<string, number> = {};
      setLocalValues((prev) => {
        const next = { ...prev };
        for (const r of changedRows) {
          const gen = r.generator ?? r.name;
          const slot = Number(
            r.slot?.value ??
              r.slot ??
              r.time_slot__slot?.value ??
              r.time_slot__slot,
          );
          if (!Number.isFinite(slot)) continue;
          const orig = origMap.get(`${gen}__${slot}`) ?? {};
          // Only apply fields that actually differ from original
          for (const [apiKey, slotField] of Object.entries(API_TO_SLOT_FIELD)) {
            if (!(apiKey in r) || r[apiKey] == null || r[apiKey] === "") continue;
            const newVal = Number(r[apiKey]);
            const origVal = orig[apiKey] == null || orig[apiKey] === "" ? null : Number(orig[apiKey]);
            if (newVal !== origVal) {
              const k = valueKey(gen, slot, slotField);
              next[k] = r[apiKey];
            }
          }
          // VC per-generator: diff against original vc
          if ("vc" in r && r.vc != null && r.vc !== "") {
            const newVc = Number(r.vc);
            const origVc = orig.vc == null || orig.vc === "" ? null : Number(orig.vc);
            if (newVc !== origVc) vcUpdates[gen] = newVc;
          }
        }
        return next;
      });
      if (Object.keys(vcUpdates).length) {
        setLocalVcValues((prev) => ({ ...prev, ...vcUpdates }));
      }
    },
    [valueKey, data],
  );

  useEffect(() => {
    setEditingCell(null);
  }, [mode]);

  // Reset local edits when data changes (new fetch)
  useEffect(() => {
    setLocalValues({});
    setLocalVcValues({});
    setEditingCell(null);
    setEditingVc(null);
  }, [data]);

  const localSize = Object.keys(localValues).length + Object.keys(localVcValues).length;

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
        No data available
      </div>
    );
  }

  return (
    <div className="relative flex flex-col rounded-xl border-none h-full">
      <div
        className={`overflow-auto flex-1 min-h-0 border-none ${isEditMode ? "rounded-t-xl" : "rounded-xl"}`}
        style={{ maxHeight }}
      >
        <table id="gridcoClassTable" className="mod_table_new">
          <thead className="sticky top-0 z-50">
            {/* ── Row 1: Frozen group label + Slot group headers ── */}
            <tr className="">
              <th
                colSpan={2}
                className="mod_th_frozen_meta_group !rounded-tl-xl"
              >
                Generator Metadata
              </th>

              {visibleSlots.map((si) => (
                <th key={si} colSpan={3} className="mod_th_slot_group">
                  Slot {si + 1}: ({TIME_RANGES[si]})
                </th>
              ))}
            </tr>

            {/* ── Row 2: Frozen sub-column headers + slot sub-column headers ── */}
            <tr>
              <th
                className="mod_th_frozen_name_col gridocClassTable cursor-pointer select-none"
                style={{ minWidth: 220, width: 220 }}
                onClick={() => toggleSort({ kind: "name" })}
              >
                <span className="inline-flex items-center gap-1">
                  Generator Name
                  <SortIcon active={sortKey?.kind === "name"} dir={sortDir} />
                </span>
              </th>
              <th
                className="mod_th_frozen_vc_col gridocClassTable cursor-pointer select-none"
                style={{ minWidth: 60, width: 60, left: 220 }}
                onClick={() => toggleSort({ kind: "vc" })}
              >
                <span className="inline-flex items-center gap-1">
                  VC
                  <SortIcon active={sortKey?.kind === "vc"} dir={sortDir} />
                </span>
              </th>

              {visibleSlots.map((si, vi) => (
                <React.Fragment key={si}>
                  {(
                    ["capacity", "technical_minimum", "class"] as SlotField[]
                  ).map((field, fi) => {
                    const label =
                      field === "capacity"
                        ? "Capacity (MW)"
                        : field === "technical_minimum"
                          ? "Tech Min (%)"
                          : "Class (1-5)";
                    const isActive =
                      sortKey?.kind === "slot" &&
                      sortKey.slot === si &&
                      sortKey.field === field;
                    const isLast = fi === 2 && vi < visibleSlots.length - 1;
                    return (
                      <th
                        key={field}
                        className={`mod_th_sub cursor-pointer select-none ${isLast ? "mod_th_sub_last" : ""}`}
                        onClick={() =>
                          toggleSort({ kind: "slot", slot: si, field })
                        }
                      >
                        <span className="inline-flex items-center gap-1">
                          {label}
                          <SortIcon active={isActive} dir={sortDir} />
                        </span>
                      </th>
                    );
                  })}
                </React.Fragment>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((gen) => (
              <tr key={gen.name} className="mod_tr_body">
                {/* Frozen: Generator Name */}
                <td
                  className="mod_td_frozen_name"
                  style={{ minWidth: 220, width: 220 }}
                >
                  {normalizeDisplayLabel(gen.name)}
                </td>
                {/* Frozen: VC */}
                {(() => {
                  const vcDisplay = localVcValues[gen.name] !== undefined ? localVcValues[gen.name] : gen.vc;
                  const vcDiffers = Object.prototype.hasOwnProperty.call(localVcValues, gen.name) && localVcValues[gen.name] !== gen.vc;
                  const isEditingThisVc = editingVc?.gen === gen.name;
                  return (
                    <td
                      className={`mod_td_frozen_vc ${isEditMode ? "cursor-pointer" : ""} relative`}
                      style={{ minWidth: 60, width: 60, left: 220 }}
                      onClick={() => { if (isEditMode && !isEditingThisVc) startEditVc(gen.name); }}
                    >
                      {isEditingThisVc ? (
                        <div className="absolute z-30 top-0 left-0 flex flex-col items-start p-1" style={{ minWidth: 120 }}>
                          {draftVcError && (
                            <div className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 mb-0.5 whitespace-nowrap">
                              {draftVcError}
                            </div>
                          )}
                          <div className="flex border border-gray-300 shadow-lg bg-white rounded">
                            <input
                              autoFocus
                              type="number"
                              inputMode="decimal"
                              step="any"
                              className="w-20 h-8 bg-[#f8f7f6] font-medium text-right pr-2 pl-2 py-1 focus:outline-none border-0 rounded-l"
                              value={draftVc}
                              onChange={(e) => { setDraftVc(e.target.value); setDraftVcError(""); }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") confirmEditVc();
                                else if (e.key === "Escape") cancelEditVc();
                              }}
                              onBlur={(e) => {
                                if (!e.currentTarget.parentElement?.contains(e.relatedTarget as Node)) cancelEditVc();
                              }}
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                              onClick={(e) => { e.stopPropagation(); confirmEditVc(); }}
                              className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white text-sm flex items-center justify-center border-0 rounded-r"
                              title="Save"
                            >
                              ✓
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          {vcDiffers && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); revertVc(gen.name); }}
                              className="text-amber-500 hover:text-amber-600 text-xs leading-none"
                              title="Revert VC"
                            >
                              ↺
                            </button>
                          )}
                          <span className={`font-medium ${vcDiffers ? "text-[#F1935C]" : ""}`}>{vcDisplay}</span>
                        </div>
                      )}
                    </td>
                  );
                })()}

                {/* Slot data */}
                {visibleSlots.map((si, vi) => {
                  const slot = gen.slots[si] ?? {
                    capacity: 0,
                    technical_minimum: 0,
                    class: 0,
                  };
                  return SLOT_FIELDS.map((field, fi) => {
                    const k = valueKey(gen.name, si, field);
                    const original = slot[field];
                    const display =
                      localValues[k] !== undefined ? localValues[k] : original;
                    const differs =
                      Object.prototype.hasOwnProperty.call(localValues, k) &&
                      localValues[k] !== original;
                    const isEditing =
                      editingCell &&
                      editingCell.gen === gen.name &&
                      editingCell.slot === si &&
                      editingCell.field === field;
                    const isLastField = fi === SLOT_FIELDS.length - 1;
                    const slotBg =
                      si % 2 === 0 ? "mod_slot_even" : "mod_slot_odd";
                    const slotEnd =
                      isLastField && vi < visibleSlots.length - 1
                        ? "mod_td_slot_end"
                        : "";

                    return (
                      <td
                        key={`${si}-${field}`}
                        className={`mod_td ${slotBg} ${slotEnd} ${isEditMode ? "cursor-pointer" : ""} relative`}
                        onClick={() => {
                          if (isEditMode && !isEditing)
                            startEdit(gen.name, si, field);
                        }}
                      >
                        {/* Revert button */}
                        {differs && !isEditing && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              revertCell(gen.name, si, field);
                            }}
                            className="absolute top-0 right-0 bg-amber-500 hover:bg-amber-600 text-white rounded-bl text-[10px] px-1.5 py-[1px] leading-tight shadow z-[9]"
                            title="Revert"
                          >
                            ↺
                          </button>
                        )}

                        {/* Display value */}
                        {!isEditing && (
                          <span
                            className={
                              differs ? "text-[#F1935C] font-semibold" : ""
                            }
                          >
                            {toRenderableValue(display)}
                          </span>
                        )}

                        {/* Inline edit input */}
                        {isEditing && (
                          <div className="absolute z-[9999] inset-0 flex items-center justify-center">
                            <div className="flex flex-col items-stretch">
                            {draftError && (
                              <div className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 mb-0.5 whitespace-nowrap">
                                {draftError}
                              </div>
                            )}
                            <div className="flex border border-gray-300 shadow-lg bg-white rounded">
                              <input
                                autoFocus
                                step={isEditing && editingCell?.field === "class" ? "1" : "any"}
                                inputMode="decimal"
                                type="number"
                                min={
                                  isEditing && editingCell?.field === "technical_minimum"
                                    ? 1
                                    : isEditing && editingCell?.field === "class"
                                      ? 1
                                      : undefined
                                }
                                max={
                                  isEditing && editingCell?.field === "technical_minimum"
                                    ? 100
                                    : isEditing && editingCell?.field === "class"
                                      ? 5
                                      : undefined
                                }
                                className="w-20 h-8 bg-[#f8f7f6] font-medium text-right pr-2 pl-2 py-1 focus:outline-none border-0 rounded-l"
                                value={draftValue}
                                onChange={(e) => {
                                  setDraftValue(e.target.value);
                                  setDraftError("");
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") confirmEdit();
                                  else if (e.key === "Escape") cancelEdit();
                                }}
                                onBlur={(e) => {
                                  if (
                                    editingCell &&
                                    !e.currentTarget.parentElement?.contains(
                                      e.relatedTarget as Node,
                                    )
                                  ) {
                                    cancelEdit();
                                  }
                                }}
                              />
                              <button
                                type="button"
                                tabIndex={-1}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmEdit();
                                }}
                                className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white text-sm flex items-center justify-center border-0 rounded-r"
                                title="Save"
                              >
                                ✓
                              </button>
                            </div>
                            </div>
                          </div>
                        )}
                      </td>
                    );
                  });
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Edit instructions (edit mode only) ── */}
      {isEditMode && (
        <div className="px-4 py-2 border-t border-[#DFEAF2] text-[11px] text-[#9B98AF] leading-relaxed bg-[#FAFAFA]">
          <span className="font-semibold text-[#525062]">How to edit: </span>
          Click any cell → type new value → press{" "}
          <kbd className="px-1 py-0.5 rounded bg-gray-100 border border-gray-300 font-mono text-[10px]">Enter</kbd>{" "}
          or <span className="text-green-600 font-medium">✓</span> to confirm,{" "}
          <kbd className="px-1 py-0.5 rounded bg-gray-100 border border-gray-300 font-mono text-[10px]">Esc</kbd>{" "}
          to cancel. &nbsp;|&nbsp; Click{" "}
          <span className="text-amber-500 font-medium">↺</span> on a cell to revert that cell. &nbsp;|&nbsp;
          Use <span className="font-medium text-[#34656D]">Batch Customize</span> for bulk edits across rows. &nbsp;|&nbsp;
          Changed values show in <span className="text-[#F1935C] font-semibold">orange</span>. Click{" "}
          <span className="font-medium text-[#34656D]">Update & Review</span> to submit.
        </div>
      )}

      {/* ── Bottom action bar (edit mode only) ── */}
      {isEditMode && (
        <div className="mod-footer-bar flex justify-between items-center">
          <div className="mod-footer-metrics">
            <div className="mod-footer-metric-box">
              <span className="mod-footer-metric-label">Total Entities :</span>
              <span className="mod-footer-metric-value">{totalEntities}</span>
            </div>
            <div className="mod-footer-divider" aria-hidden="true" />
            <div className="mod-footer-metric-box">
              <span className="mod-footer-metric-label">Modifications :</span>
              <span className="mod-footer-metric-value mod-footer-metric-value--accent">
                +{localSize}
              </span>
            </div>
          </div>

          <div className="mod-footer-actions">
            <button
              onClick={() => setModalOpen(false)}
              className="mod-footer-cancel"
            >
              Cancel
            </button>
            <button
              onClick={revertAll}
              disabled={localSize === 0 || loading}
              className="mod-footer-revert"
            >
              <Undo className="w-4 h-4" />
              Revert
            </button>
            <button
              onClick={handleUpdateClick}
              className="mod-footer-update"
              disabled={localSize === 0 || loading}
            >
              {loading ? "Saving..." : "Update & Review"}
            </button>
          </div>
        </div>
      )}

      {/* ── Bulk filter modal ── */}
      <ModFilter
        loading={loading}
        uniqueGeneratorTypes={uniqueGeneratorType}
        uniqueOwnership={uniqueOwnership}
        uniqueBlocks={uniqueBlock}
        isOpen={modalFilter}
        handleSubmit={handleUpdateReview}
        onLocalApply={applyBulkChanges}
        onClose={() => (setModalFilter ? setModalFilter(false) : null)}
        data={data ?? []}
      />
    </div>
  );
}
